// src/pages/PrivacyPolicyPage.tsx
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type Lang = 'ja' | 'zh';
type LJ = { ja: string; zh: string };

const copy = {
  title: { ja: '個人情報保護方針', zh: '隐私政策' } as LJ,
  intro: {
    ja: 'SMHC株式会社（以下「当社」）は、オンライン診療プラットフォーム（Melodis Clinic）を運営するにあたり、個人情報の重要性を認識し、適切な保護・管理・運用に努めます。本方針は、当社が取り扱う個人情報の基本的な考え方を示すものです。',
    zh: 'SMHC株式会社（以下简称“本公司”）在运营在线诊疗平台（Melodis Clinic）过程中，深知个人信息的重要性，致力于其妥善保护、管理与使用。本文为本公司处理个人信息的基本方针。',
  } as LJ,

  // 1. 基本方針
  s1_title: { ja: '1. 基本方針', zh: '1. 基本方针' } as LJ,
  s1_body: {
    ja: '当社は、法令・ガイドラインに基づき、取得目的を明確化したうえで適正な手段により個人情報を取得し、目的達成に必要な範囲に限定して利用します。目的外利用を防止するための措置を講じ、安全管理体制を継続的に改善します。',
    zh: '本公司依据相关法律与指引，在明示目的前提下通过正当方式获取个人信息，并将使用限定于实现目的所必需的范围内。我们采取措施防止目的外使用，并持续改进安全管理体系。',
  } as LJ,

  // 2. 取得・適用範囲
  s2_title: { ja: '2. 取得対象と適用範囲', zh: '2. 获取对象与适用范围' } as LJ,
  s2_body: {
    ja: '本方針は、当社サービスの利用者（患者・会員・お問い合わせのご担当者等）に関する個人情報に適用されます。オンライン診療に必要な情報（氏名、連絡先、本人確認情報、予約情報、診療関連情報、決済情報、端末・クッキー等の識別子）を、必要最小限の範囲で取り扱います。',
    zh: '本方针适用于本公司服务使用者（患者、会员及咨询联系人等）的个人信息。我们仅在必要范围内处理在线诊疗所需的信息，如姓名、联系方式、身份核验信息、预约信息、诊疗相关信息、支付信息、设备与 Cookie 等标识符。',
  } as LJ,

  // 3. 利用目的
  s3_title: { ja: '3. 個人情報の利用目的', zh: '3. 个人信息的使用目的' } as LJ,
  s3_list: {
    ja: [
      'オンライン診療の提供、予約管理、問診・診療記録の作成・保管',
      '本人確認、不正利用・なりすまし防止、セキュリティの確保',
      '決済処理、領収書等の発行および事務手続',
      'お問い合わせ対応、サポートの提供、重要なお知らせの通知',
      'サービスの品質向上、統計的分析（個人が特定されない形式）',
      '法令に基づく対応（照会・報告など）',
    ],
    zh: [
      '提供在线诊疗，进行预约管理，制作与保存问诊与诊疗记录',
      '身份核验、防止不当使用与冒用，确保安全',
      '支付处理、票据开具及相关事务手续',
      '咨询响应、支持服务，及重要通知的发送',
      '为提升服务质量而进行统计分析（以无法识别个人的形式）',
      '根据法律之要求进行配合与处理',
    ],
  } as { ja: string[]; zh: string[] },

  // 4. 第三者提供
  s4_title: { ja: '4. 第三者提供', zh: '4. 向第三方提供' } as LJ,
  s4_body: {
    ja: [
      '当社は、次の場合を除き個人情報を第三者に提供しません。',
      '（1）本人の同意がある場合',
      '（2）法令に基づく場合',
      '（3）人命・身体・財産の保護に必要で同意取得が困難な場合',
      '（4）公衆衛生の向上等に特に必要な場合',
      '（5）国・地方公共団体等の法令に定める事務へ協力が必要な場合',
    ],
    zh: [
      '除以下情形外，我们不向第三方提供个人信息：',
      '（1）已取得本人同意',
      '（2）基于法律要求',
      '（3）为保护生命、身体或财产而难以取得同意',
      '（4）为提升公共卫生等特别必要之情形',
      '（5）为配合法定事务之执行而有必要时',
    ],
  } as { ja: string[]; zh: string[] },

  // 5. 委託
  s5_title: { ja: '5. 委託先の監督', zh: '5. 受托方管理' } as LJ,
  s5_body: {
    ja: '業務委託に伴い個人情報の取扱いを委託する場合は、委託先の選定基準を設け、契約等により適切な安全管理措置が講じられるよう監督します。',
    zh: '如因业务外包而委托处理个人信息，我们将设定遴选标准，并通过合同等方式对受托方进行监督，要求其实施适当的安全管理措施。',
  } as LJ,

  // 6. 安全管理措置
  s6_title: { ja: '6. 安全管理', zh: '6. 安全管理' } as LJ,
  s6_list: {
    ja: [
      'アクセス制御・多要素認証・権限の最小化',
      '通信・保管時の暗号化、バックアップ運用',
      '監査ログの取得・点検、社内教育の実施',
      '脆弱性対応、マルウェア対策、ネットワーク防御',
    ],
    zh: [
      '访问控制、多因素认证与最小权限',
      '传输与存储加密、备份管理',
      '审计日志与检查、员工安全培训',
      '漏洞处置、恶意软件防护与网络防御',
    ],
  } as { ja: string[]; zh: string[] },

  // 7. Cookie / 端末識別子
  s7_title: { ja: '7. Cookie・識別子の利用', zh: '7. Cookie 与标识符' } as LJ,
  s7_body: {
    ja: '利便性向上や不正対策、統計分析のためCookie・端末識別子等を利用することがあります。ブラウザ設定等で無効化できますが、一部機能に制約が生じる場合があります。',
    zh: '为提升便利性、进行反滥用与统计分析，本站可能使用 Cookie 与设备标识符。你可在浏览器中进行限制或关闭，但可能影响部分功能的正常使用。',
  } as LJ,

  // 8. 開示等
  s8_title: {
    ja: '8. 開示・訂正・削除・利用停止等のご請求',
    zh: '8. 个人信息的查询、更正、删除与停止使用',
  } as LJ,
  s8_body: {
    ja: 'ご本人（または代理人）からの開示、訂正、削除、利用停止等のご請求には、適切な本人確認のうえ、法令に従い速やかに対応します。手続の詳細はお問い合わせページよりご連絡ください。',
    zh: '如需查询、更正、删除或停止使用个人信息，我们将在完成身份核验后，依法及时处理。请通过站内“联系我们”页面获取办理方式与所需材料。',
  } as LJ,

  // 9. お問い合わせ
  s9_title: { ja: '9. お問い合わせ窓口', zh: '9. 联系方式' } as LJ,
  s9_body: {
    ja: '本方針や個人情報の取扱いに関するお問い合わせは、以下の窓口またはお問い合わせページよりご連絡ください。',
    zh: '关于本方针与个人信息处理的咨询，请联系如下地址，或通过站内“联系我们”页面提交。',
  } as LJ,
  address_label: { ja: '所在地', zh: '地址' } as LJ,
  address_value: {
    ja: '〒110-0023 東京都新宿区西新宿7-21-1 新宿ロイヤルビル201',
    zh: '〒110-0023 日本东京都新宿区西新宿7-21-1 新宿皇家大楼201',
  } as LJ,
  contact_cta: { ja: 'お問い合わせへ', zh: '前往联系我们' } as LJ,
};

const L = (x: LJ, lang: Lang) => x[lang];

const PrivacyPolicyPage: React.FC = () => {
  const { i18n } = useTranslation();
  const lang: Lang = useMemo(
    () => (i18n.language?.toLowerCase().startsWith('zh') ? 'zh' : 'ja'),
    [i18n.language]
  );

  const list = (arr: string[]) => (
    <ul className="list-disc pl-6 text-slate-700 space-y-2">
      {arr.map((li, i) => (
        <li key={i}>{li}</li>
      ))}
    </ul>
  );

  return (
    <div className="pt-0 sm:pt-14 pb-10 sm:pb-20 bg-gray-50 font-sans">
      <Helmet>
        <title>{L(copy.title, lang)} - Melodis Clinic</title>
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-4xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          <h1 className="text-xl md:text-2xl font-semibold text-slate-800 mb-6 text-center">
            {L(copy.title, lang)}
          </h1>

          <p className="text-slate-700 leading-relaxed mb-8 text-base md:text-lg">
            {L(copy.intro, lang)}
          </p>

          {/* 1 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s1_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.s1_body, lang)}</p>
          </section>

          {/* 2 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s2_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.s2_body, lang)}</p>
          </section>

          {/* 3 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s3_title, lang)}</h2>
            {list(copy.s3_list[lang])}
          </section>

          {/* 4 ——— 修复处：数组用 list() 渲染 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s4_title, lang)}</h2>
            {list(copy.s4_body[lang])}
          </section>

          {/* 5 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s5_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.s5_body, lang)}</p>
          </section>

          {/* 6 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s6_title, lang)}</h2>
            {list(copy.s6_list[lang])}
          </section>

          {/* 7 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s7_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.s7_body, lang)}</p>
          </section>

          {/* 8 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s8_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.s8_body, lang)}</p>
          </section>

          {/* 9 お問い合わせ */}
          <section>
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.s9_title, lang)}</h2>
            <div className="text-slate-700 leading-relaxed space-y-3">
              <p>{L(copy.s9_body, lang)}</p>
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                <div className="text-sm text-slate-500">{L(copy.address_label, lang)}</div>
                <div className="font-medium">{L(copy.address_value, lang)}</div>
              </div>
              <div className="flex justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-4 py-2 rounded-lg border border-coral/20 hover:border-coral/30 text-coral hover:bg-coral/5 transition"
                >
                  {L(copy.contact_cta, lang)}
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
